import { Component, ElementRef, effect, inject, input, signal, viewChild } from '@angular/core';
import { DecimalPipe, DatePipe } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { MiddlewareApi } from '../core/services/middleware-api.service';
import { OrderReceipt } from '../core/models';

// The order Receipt view, shared by every host that shows one (the order detail modal's Receipt tab,
// the per-row receipt dialog on the orders lists). Self-loading: give it the order's identity
// (location / external id / source) and it fetches, renders, and prints — so no host ever duplicates
// the paper template or the fetch-and-error plumbing. The GET is a read-through (perm orders:read);
// viewing changes nothing anywhere.

// The receipt "paper". Deliberately paper-white in BOTH themes: it depicts the physical receipt the
// store's printer produces, and paper does not have a dark mode — the chrome around it stays
// theme-aware. Kept as a module-level constant (not only component styles) because printReceipt()
// copies the rendered paper into a bare window and needs the SAME rules there, unscoped: one string,
// two consumers, no drift between what is shown and what is printed.
const RECEIPT_CSS = `
.receipt-paper {
    width: 20rem;
    max-width: 100%;
    background: #fdfdfb;
    color: #1c1c1c;
    font-family: 'Courier New', ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
    line-height: 1.45;
    padding: 1.25rem 1rem 1.5rem;
    border-radius: 2px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3), 0 4px 14px rgba(0, 0, 0, 0.12);
}
.receipt-paper .rc-center { text-align: center; }
.receipt-paper .rc-store { font-weight: 700; font-size: 13px; text-transform: uppercase; letter-spacing: 0.06em; }
.receipt-paper .rc-rule { border-top: 1px dashed #8a8a8a; margin: 8px 0; }
.receipt-paper .rc-row { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; }
.receipt-paper .rc-name { flex: 1 1 auto; word-break: break-word; }
.receipt-paper .rc-amt { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
.receipt-paper .rc-sub { padding-left: 14px; color: #555; font-size: 11px; }
.receipt-paper .rc-total { font-weight: 700; font-size: 13px; margin-top: 4px; }
.receipt-paper .rc-mono-sm { font-size: 11px; word-break: break-all; }
.receipt-paper .rc-note { white-space: pre-line; word-break: break-word; }
`;

// Extra rules for the print window only: the bare page holds nothing but the paper, so printing it
// prints JUST the receipt — no dialog chrome, no table, no nav.
const RECEIPT_PRINT_CSS = `
body { margin: 0; padding: 16px; background: #fff; display: flex; justify-content: center; }
@media print {
    body { padding: 0; }
    .receipt-paper { box-shadow: none; border-radius: 0; }
}
`;

@Component({
    selector: 'app-order-receipt',
    standalone: true,
    imports: [ButtonModule, MessageModule, DecimalPipe, DatePipe],
    styles: [RECEIPT_CSS],
    template: `
        @if (loading()) {
            <p class="text-sm text-surface-500">Loading receipt…</p>
        } @else if (error()) {
            <p-message severity="warn">{{ error() }}</p-message>
        } @else if (receipt(); as r) {
            <div class="flex flex-col items-center gap-3">
                <!-- Paper-white on purpose, in both themes: it depicts the physical receipt. -->
                <div class="receipt-paper" #paper>
                    <div class="rc-center rc-store">{{ r.storeName }}</div>
                    <div class="rc-center">Order {{ r.orderNumber }}</div>
                    <div class="rc-center">{{ r.placedAt | date: 'MM/dd/yy h:mm a' }}</div>
                    <div class="rc-rule"></div>
                    @for (it of r.ticket.items; track $index) {
                        <div class="rc-row">
                            <span class="rc-name">{{ it.quantity | number: '1.0-2' }} × {{ it.name }}</span>
                            <span class="rc-amt">{{ it.price * it.quantity | number: '1.2-2' }}</span>
                        </div>
                        @if (it.quantity !== 1) {
                            <div class="rc-sub">&#64; {{ it.price | number: '1.2-2' }} each</div>
                        }
                    }
                    @if (!r.ticket.items.length) {
                        <div class="rc-center">(no line items on the ticket)</div>
                    }
                    <div class="rc-rule"></div>
                    <div class="rc-row"><span>Subtotal</span><span class="rc-amt">{{ r.ticket.subtotal | number: '1.2-2' }}</span></div>
                    @if (r.discountTotal !== 0) {
                        <div class="rc-row"><span>Discount</span><span class="rc-amt">−{{ abs(r.discountTotal) | number: '1.2-2' }}</span></div>
                    }
                    @if (r.feeTotal !== 0) {
                        <div class="rc-row"><span>Fees</span><span class="rc-amt">{{ r.feeTotal | number: '1.2-2' }}</span></div>
                    }
                    @if (r.shippingTotal !== 0) {
                        <div class="rc-row"><span>Shipping</span><span class="rc-amt">{{ r.shippingTotal | number: '1.2-2' }}</span></div>
                    }
                    @if (r.ticket.tax !== 0) {
                        <div class="rc-row"><span>Tax</span><span class="rc-amt">{{ r.ticket.tax | number: '1.2-2' }}</span></div>
                    }
                    @if (r.ticket.tip !== 0) {
                        <div class="rc-row"><span>Tip</span><span class="rc-amt">{{ r.ticket.tip | number: '1.2-2' }}</span></div>
                    }
                    <div class="rc-row rc-total"><span>TOTAL</span><span class="rc-amt">{{ r.currency }} {{ r.ticket.total | number: '1.2-2' }}</span></div>
                    @if (r.payment; as pay) {
                        <div class="rc-rule"></div>
                        <div class="rc-row">
                            <span>{{ pay.brand || pay.method || 'Payment' }}@if (pay.last4) { ····{{ pay.last4 }}}</span>
                            <span class="rc-amt">{{ pay.amount | number: '1.2-2' }}</span>
                        </div>
                    } @else if (r.ticket.paymentMethod) {
                        <div class="rc-rule"></div>
                        <div class="rc-row"><span>Paid — {{ r.ticket.paymentMethod }}</span></div>
                    }
                    @if (r.fulfillmentType || r.trackingRef) {
                        <div class="rc-rule"></div>
                        @if (r.fulfillmentType) { <div class="rc-center">{{ r.fulfillmentType }}</div> }
                        @if (r.trackingRef) { <div class="rc-center rc-mono-sm">ref {{ r.trackingRef }}</div> }
                    }
                    @if (r.ticket.customerName || r.ticket.street) {
                        <div class="rc-rule"></div>
                        @if (r.ticket.customerName) { <div class="rc-center">{{ r.ticket.customerName }}</div> }
                        @if (r.ticket.phone) { <div class="rc-center">{{ r.ticket.phone }}</div> }
                        @if (r.ticket.street) { <div class="rc-center">{{ r.ticket.street }}</div> }
                        @if (r.ticket.city || r.ticket.postcode) {
                            <div class="rc-center">{{ r.ticket.city }}@if (r.ticket.region) {, {{ r.ticket.region }}} {{ r.ticket.postcode }}</div>
                        }
                    }
                    @if (r.deliveryComment) {
                        <div class="rc-rule"></div>
                        <div class="rc-center">Special instructions</div>
                        <div class="rc-note">{{ r.deliveryComment }}</div>
                    }
                    <div class="rc-rule"></div>
                    @if (r.supportReference) { <div class="rc-center rc-mono-sm">Support ref: {{ r.supportReference }}</div> }
                    <div class="rc-center">Thank you!</div>
                </div>
                <div class="flex items-center gap-2">
                    <p-button label="Print" icon="pi pi-print" size="small" [outlined]="true" (onClick)="printReceipt()" />
                    @if (!r.fromStoredTicket) {
                        <span class="text-xs text-surface-500 dark:text-surface-400">
                            Preview only — no print ticket attached yet
                        </span>
                    }
                </div>
            </div>
        }
    `
})
export class OrderReceiptView {
    private readonly api = inject(MiddlewareApi);

    // The order's identity — everything this view needs to fetch its receipt. Source rides along
    // because an external id can be shared across sources at a location, and the receipt GET without
    // source then returns HTTP 400. Every list row and detail carries it.
    readonly locationId = input.required<string>();
    readonly externalOrderId = input.required<string>();
    readonly source = input<string>('');

    readonly receipt = signal<OrderReceipt | null>(null);
    readonly loading = signal(false);
    readonly error = signal<string | null>(null);

    // The rendered paper, by template ref rather than a document-wide id: two hosts can exist in the
    // same page (detail tab + row dialog), and each must print ITS paper.
    private readonly paper = viewChild<ElementRef<HTMLElement>>('paper');

    private loadedKey: string | null = null;

    constructor() {
        // Load whenever the identity resolves or changes — hosts just bind inputs and are done.
        effect(() => {
            const loc = this.locationId();
            const id = this.externalOrderId();
            const src = this.source();
            const key = `${loc}|${id}|${src}`;
            if (!loc || !id || key === this.loadedKey) return;
            this.loadedKey = key;
            this.load(loc, id, src);
        });
    }

    /** The receipt content. A 422 carries the API's own reason (no ticket AND unreadable payload), and
     *  it renders verbatim in place of the paper — an honest refusal beats a blank receipt. */
    private load(loc: string, id: string, src: string): void {
        this.loading.set(true);
        this.error.set(null);
        this.receipt.set(null);
        this.api.getOrderReceipt(loc, id, src || undefined).subscribe({
            next: (r) => { this.receipt.set(r); this.loading.set(false); },
            error: (e) => {
                this.receipt.set(null);
                this.loading.set(false);
                this.error.set(this.describe(e));
            }
        });
    }

    /** Prints JUST the receipt: the rendered paper is copied into a bare window holding nothing else,
     *  so the browser's print dialog sees the receipt and not the dialog, table or nav around it. The
     *  markup is lifted from the DOM (already escaped by Angular's renderer) and styled with the SAME
     *  css constant the on-screen paper uses — what prints is what was shown. */
    printReceipt(): void {
        const paper = this.paper()?.nativeElement;
        if (!paper) return;
        const w = window.open('', '_blank', 'width=440,height=680');
        if (!w) return;
        w.document.write(
            `<!doctype html><html><head><meta charset="utf-8"><title>Receipt</title>`
            + `<style>${RECEIPT_CSS}${RECEIPT_PRINT_CSS}</style></head>`
            + `<body>${paper.outerHTML}</body></html>`);
        w.document.close();
        // document.write parses synchronously, but print() before the popup's first paint yields a
        // blank page on some engines (Firefox notably) — wait one frame so layout exists, then print.
        w.requestAnimationFrame(() => {
            w.focus();
            w.print();
        });
    }

    /** Discounts arrive positive from some sources and negative from others; the receipt always shows
     *  "−amount", so normalize here rather than trusting the sign convention. */
    abs(v: number): number {
        return Math.abs(v);
    }

    private describe(e: any): string {
        if (e?.status === 0) return 'Cannot reach the API.';
        if (e?.status === 401) return 'Unauthorized — signing in…';
        if (e?.status === 403) return 'Forbidden — your role lacks the orders:read permission.';
        const server = e?.error?.detail ?? e?.error?.message ?? e?.error?.title;
        if (e?.status === 400) {
            return server ?? 'Bad request — this order id may exist under more than one source; a source is required.';
        }
        return server ?? `Error ${e?.status ?? ''}: ${e?.message ?? 'request failed'}`;
    }
}
